import { UserService } from './../../../../core/services/user.service';
import { RECOVER_RECORDING_DIALOG_CONFIG } from '../../../../shared/config/floating-ai-dialog.config';
import { PatientService } from './../../../../core/services/patient.service';
import { SpeechRecognitionService } from 'src/app/core/services/speech-recognition.service';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { map, takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { GroupService } from './../../../../core/services/group.service';
import { PatientAiService } from './../../../../core/services/patient-ai.service';
import { RecoverRecordingDialogComponent } from 'src/app/shared/components/recover-recording-dialog/recover-recording-dialog.component';


@Component({
  selector: 'app-patient-ai-request-list',
  templateUrl: './patient-ai-request-list.component.html',
  styleUrls: ['./patient-ai-request-list.component.scss']
})
export class PatientAiRequestListComponent extends BaseComponent implements OnInit {
  @Input() initialExpanded?: boolean;
  expanded?: boolean;
  @Output() expandedChange = new EventEmitter<boolean>();
  requestData!: any[] | null;
  user: any;
  patient: any;
  /** When false, hide recover/download UI; undefined from API is treated as allowed (`!== false`). */
  canDownloadRecordings = true;

  constructor(
    private router: Router,
    private userService: UserService,
    private groupService: GroupService,
    private patientAIService: PatientAiService,
    private speechService: SpeechRecognitionService,
    private patientService: PatientService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.loadRequestData();

    // Subscribe to speech commands for loading specific AI requests by position
    this.speechService.getDetectedCommand().subscribe(command => {
      if (command.command === 'LOAD_REQUEST_BY_POSITION' && typeof command.payload === 'number') {
        this.loadRequestByIndex(command.payload);
      }
    });

    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile: any) => {
        this.user = profile;
      });

    this.patientService.currentPatient$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedPatient: any) => {
        this.patient = selectedPatient;
      });

    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((results: any[] | null) => {
        const uid = this.userService.user?.id;
        if (!results?.length || !uid) {
          return;
        }
        const row = results.find((u: any) => u.userId === uid);
        this.canDownloadRecordings = row?.canDownloadRecordings !== false;
      });
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
  }

  loadRequestByIndex(index: number) {
    if (this.requestData && this.requestData.length > 0) {
      // Flatten the grouped data into a single list for indexing
      const flattenedRequests = this.requestData.flatMap(group => group.items);

      // Adjust index for "last" or out-of-bounds cases
      const adjustedIndex = index === -1 ? flattenedRequests.length - 1 : index;

      if (adjustedIndex >= 0 && adjustedIndex < flattenedRequests.length) {
        // Load the request at the specified position
        this.loadRequest(flattenedRequests[adjustedIndex]);
      } else {
        console.warn('Requested position is out of bounds');
      }
    }
  }

  loadRequestData(): void {
    this.patientAIService.patientAIRequests$
      .pipe(
        map((requests) => {
          return this.groupAndSortRequests(requests || []);
        }),
        takeUntil(this.destroy$),
      ).subscribe({
        next: (groupedData) => {
          this.requestData = groupedData;
        },
        error: (err) => {
          console.error('Error loading AI requests:', err);
          this.requestData = [];  // Optionally reset or handle errors as needed
        }
      });
  }

  groupAndSortRequests(requests: any): any[] {

    if (!requests || !requests.length) {
      return requests;
    }

    // Group notes by formatted date (no need to format date here since we're just grouping)
    const grouped = _.groupBy(requests, item => item.date ? moment(item.date).format('YYYY-MM-DD') : moment(item.createdAt).format('YYYY-MM-DD'));

    // Sort the groups by date in descending order and transform into an array
    return _.chain(grouped)
      .map((value, key) => ({
        date: key, items: value
      }))
      // Use the ISO date string directly for sorting
      .orderBy(group => group.date, 'desc')
      .value();

  }

  loadRequest(request: any) {
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    this.patientAIService.setEditingAIRequest(request);
    this.router.navigate(['/patients', {
      outlets: {
        patient: ['edit-request', request.id],
      }
    }]);

  }

  recoverRecordings(ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    if (!this.canDownloadRecordings) {
      return;
    }
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    this.dialog.open(RecoverRecordingDialogComponent, {
      ...RECOVER_RECORDING_DIALOG_CONFIG,
      data: this.patient,
    });
  }

}
